import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import StepIndicator from "../../components/employee/step-indicator";
import ProgressBar from "../../components/employee/progressbar";
import PersonalInformation from "../../components/employee/personalInfo";
import OrganisationForm from "../../components/employee/organisation";
import EducationForm from "../../components/employee/education";
import Experience from "../../components/employee/experience";
import Awards from "../../components/employee/awards";
import { SuccessModal } from "../../components/modal";
import EmployeeToggleHeader from "./employeeHeader";
import axios from "../../api/httpClient";
import { getApiBaseUrl } from "../../data/authStorage";

const defaultPersonal = {
  firstName: "",
  lastName: "",
  personalEmail: "",
  officialEmail: "",
  phone: "",
  maritalStatus: "",
  gender: "",
  dob: "",
  employeeId: "",
  residentialAddress: "",
  lga: "",
  stateOrigin: "",
  stateResidence: "",
};

const defaultEducation = {
  school: "",
  degree: "",
  course: "",
  qualificationClass: "",
  startYear: "",
  endYear: "",
};

const defaultAwards = {
  nameOfAward: "",
  issuedBy: "",
  awardDate: "",
  expiryDate: "",
  certification: "",
  certificationBody: "",
  certificateExpiryDate: "",
};

const defaultOrganization = {
  department: "",
  role: "",
  employeeId: "",
  employmentType: "",
  dateJoined: "",
  level: "",
  branch: "",
  position: "",
  pensionProvider: "",
  pensionNumber: "",
  hmo: "",
  hmoId: "",
  hmoNumber: "",
  hmoStatus: "",
  hmoPlan: "",
  hmoHospital: "",
};

const emptyExperience = {
  company: "",
  position: "",
  employmentType: "",
  start: "",
  end: "",
  description: "",
  portfolioLink: "",
  documents: [],
};

const NewEmployee = () => {
  const apiBaseUrl = getApiBaseUrl();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [modal, setModal] = useState(false);
  const [showOffboarding, setShowOffboarding] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [personalData, setPersonalData] = useState(defaultPersonal);
  const [educationData, setEducationData] = useState([{ ...defaultEducation }]);
  const [experienceData, setExperienceData] = useState([emptyExperience]);
  const [awardsData, setAwardsData] = useState(defaultAwards);
  const [organizationData, setOrganizationData] = useState(defaultOrganization);
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [generatedEmployeeId, setGeneratedEmployeeId] = useState("");
  const [jobTemplates, setJobTemplates] = useState([]);
  const [jobTemplateId, setJobTemplateId] = useState("");

  useEffect(() => {
    if (!apiBaseUrl) return;
    axios.get(`${apiBaseUrl}/job-descriptions/templates`)
      .then((response) => setJobTemplates((response.data.templates || []).filter((item) => item.isActive)))
      .catch(() => setJobTemplates([]));
  }, [apiBaseUrl]);

  const totalSteps = 5;

  const hasValidEducation = useMemo(
    () =>
      Array.isArray(educationData) &&
      educationData.length > 0 &&
      educationData.every(
        (item) =>
          `${item.school || ""}`.trim() &&
          `${item.degree || ""}`.trim() &&
          `${item.course || ""}`.trim() &&
          `${item.qualificationClass || ""}`.trim() &&
          `${item.startYear || ""}`.trim() &&
          `${item.endYear || ""}`.trim(),
      ),
    [educationData],
  );

  const hasValidPersonal = useMemo(() => {
    const requiredPersonal = [
      "firstName",
      "lastName",
      "personalEmail",
      "officialEmail",
      "phone",
      "maritalStatus",
      "gender",
      "dob",
      "residentialAddress",
      "lga",
      "stateOrigin",
      "stateResidence",
    ];

    return requiredPersonal.every((field) => `${personalData[field] || ""}`.trim());
  }, [personalData]);

  const personalErrors = useMemo(() => {
    const errors = {};
    if (!`${personalData.firstName || ""}`.trim()) errors.firstName = "First name is required.";
    if (!`${personalData.lastName || ""}`.trim()) errors.lastName = "Last name is required.";
    if (!`${personalData.personalEmail || ""}`.trim()) errors.personalEmail = "Personal email is required.";
    if (!`${personalData.officialEmail || ""}`.trim()) errors.officialEmail = "Official email is required.";
    if (!`${personalData.phone || ""}`.trim()) errors.phone = "Phone number is required.";
    if (!`${personalData.gender || ""}`.trim()) errors.gender = "Gender is required.";
    if (!`${personalData.maritalStatus || ""}`.trim()) errors.maritalStatus = "Marital status is required.";
    if (!`${personalData.dob || ""}`.trim()) errors.dob = "Date of birth is required.";
    if (!`${personalData.residentialAddress || ""}`.trim()) errors.residentialAddress = "Residential address is required.";
    if (!`${personalData.lga || ""}`.trim()) errors.lga = "Local government area is required.";
    if (!`${personalData.stateOrigin || ""}`.trim()) errors.stateOrigin = "State of origin is required.";
    if (!`${personalData.stateResidence || ""}`.trim()) errors.stateResidence = "State of residence is required.";
    return errors;
  }, [personalData]);

  const educationErrors = useMemo(
    () =>
      (educationData || []).map((item) => {
        const errors = {};
        if (!`${item.school || ""}`.trim()) errors.school = "School is required.";
        if (!`${item.degree || ""}`.trim()) errors.degree = "Degree is required.";
        if (!`${item.course || ""}`.trim()) errors.course = "Course is required.";
        if (!`${item.qualificationClass || ""}`.trim()) errors.qualificationClass = "Class of degree is required.";
        if (!`${item.startYear || ""}`.trim()) errors.startYear = "Start year is required.";
        if (!`${item.endYear || ""}`.trim()) errors.endYear = "End year is required.";
        return errors;
      }),
    [educationData],
  );

  const organizationErrors = useMemo(() => {
    const errors = {};
    if (!`${organizationData.department || ""}`.trim()) errors.department = "Department is required.";
    if (!`${organizationData.role || ""}`.trim()) errors.role = "Role is required.";
    if (!`${organizationData.level || ""}`.trim()) errors.level = "Level is required.";
    if (!`${organizationData.branch || ""}`.trim()) errors.branch = "Branch is required.";
    if (!`${organizationData.employmentType || ""}`.trim()) errors.employmentType = "Employment type is required.";
    if (!`${organizationData.dateJoined || ""}`.trim()) errors.dateJoined = "Date joined is required.";
    return errors;
  }, [organizationData]);

  const hasValidExperience = useMemo(
    () => true,
    [],
  );

  const hasValidOrganization = useMemo(() => {
    const requiredOrganization = [
      "department",
      "role",
      "level",
      "branch",
      "employmentType",
      "dateJoined",
    ];

    return requiredOrganization.every((field) => `${organizationData[field] || ""}`.trim());
  }, [organizationData]);

  const canProceedCurrentStep = useMemo(() => {
    if (currentStep === 1) return hasValidPersonal;
    if (currentStep === 2) return hasValidEducation;
    if (currentStep === 3) return hasValidExperience;
    if (currentStep === 4) return true;
    if (currentStep === 5) return hasValidOrganization;
    return false;
  }, [currentStep, hasValidPersonal, hasValidEducation, hasValidExperience, hasValidOrganization]);

  const validateBeforeSubmit = () => {
    const requiredPersonal = [
      "firstName",
      "lastName",
      "personalEmail",
      "officialEmail",
      "phone",
      "maritalStatus",
      "gender",
      "dob",
      "residentialAddress",
      "lga",
      "stateOrigin",
      "stateResidence",
    ];

    const missingPersonal = requiredPersonal.find((field) => !`${personalData[field] || ""}`.trim());
    if (missingPersonal) {
      return "Please complete all required Personal Information fields.";
    }

    const requiredOrganization = [
      "department",
      "role",
      "level",
      "branch",
      "employmentType",
      "dateJoined",
    ];

    const missingOrganization = requiredOrganization.find((field) => !`${organizationData[field] || ""}`.trim());
    if (missingOrganization) {
      return "Please complete all required Organization fields.";
    }

    if (!hasValidEducation) {
      return "Please enter the required Education section details.";
    }

    return "";
  };

  const handleSubmit = async () => {
    const validationError = validateBeforeSubmit();
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    if (!apiBaseUrl) {
      setSubmitError("API base URL is not configured.");
      return;
    }

    try {
      setIsSubmitting(true);
      setSubmitError("");

      const formData = new FormData();

      formData.append("firstName", personalData.firstName.trim());
      formData.append("lastName", personalData.lastName.trim());
      formData.append("personalEmail", personalData.personalEmail.trim());
      formData.append("officialEmail", personalData.officialEmail.trim());
      formData.append("phone", personalData.phone.trim());
      formData.append("gender", personalData.gender.trim());
      formData.append("maritalStatus", personalData.maritalStatus.trim());
      formData.append("dob", personalData.dob);
      formData.append("residentialAddress", personalData.residentialAddress.trim());
      formData.append("lga", personalData.lga.trim());
      formData.append("stateOrigin", personalData.stateOrigin.trim());
      formData.append("stateResidence", personalData.stateResidence.trim());

      formData.append("employmentType", organizationData.employmentType.trim());
      formData.append("dateJoined", organizationData.dateJoined);
      formData.append("departmentId", organizationData.department);
      formData.append("departmentIds", JSON.stringify([
        ...new Set([organizationData.department, ...(organizationData.departmentIds || [])].filter(Boolean)),
      ]));
      formData.append("roleId", organizationData.role);
      formData.append("levelId", organizationData.level);
      if (`${organizationData.position || ""}`.trim()) {
        formData.append("positionId", organizationData.position);
      }
      formData.append("branchId", organizationData.branch);

      if (`${organizationData.pensionProvider || ""}`.trim()) {
        formData.append("pensionProvider", organizationData.pensionProvider.trim());
      }
      if (`${organizationData.pensionNumber || ""}`.trim()) {
        formData.append("pensionNumber", organizationData.pensionNumber.trim());
      }

      if (`${organizationData.hmo || ""}`.trim()) {
        formData.append("hmo", organizationData.hmo.trim());
      }
      if (`${organizationData.hmoId || ""}`.trim()) {
        formData.append("hmoId", organizationData.hmoId.trim());
      }
      if (`${organizationData.hmoNumber || ""}`.trim()) {
        formData.append("hmoNumber", organizationData.hmoNumber.trim());
      }
      if (`${organizationData.hmoStatus || ""}`.trim()) {
        formData.append("hmoStatus", organizationData.hmoStatus.trim());
      }
      if (`${organizationData.hmoPlan || ""}`.trim()) {
        formData.append("hmoPlan", organizationData.hmoPlan.trim());
      }
      if (`${organizationData.hmoHospital || ""}`.trim()) {
        formData.append("hmoHospital", organizationData.hmoHospital.trim());
      }

      formData.append("educationRecords", JSON.stringify(educationData || []));

      const normalizedExperience = (experienceData || [])
        .filter((item) =>
          [
            item.company,
            item.position,
            item.employmentType,
            item.start,
            item.end,
            item.description,
            item.portfolioLink,
          ].some((value) => `${value || ""}`.trim()) || (item.documents || []).length > 0,
        )
        .map((item) => ({
          ...item,
          documents: (item.documents || []).map((doc) => doc?.name).filter(Boolean),
        }));

      formData.append("experienceRecords", JSON.stringify(normalizedExperience));
      formData.append("awardsRecords", JSON.stringify([awardsData]));

      if (profilePhotoFile) {
        formData.append("profilePhoto", profilePhotoFile);
      }

      const response = await axios.post(`${apiBaseUrl}/organizations/employees/onboarding`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (jobTemplateId && response?.data?.employee?.id) {
        await axios.post(`${apiBaseUrl}/job-descriptions/assignments`, {
          employeeId: response.data.employee.id,
          templateId: jobTemplateId,
        });
      }

      setGeneratedEmployeeId(response?.data?.employee?.employeeNumber || "");

      setModal(true);
    } catch (error) {
      const message = error?.response?.data?.message || "Unable to submit onboarding details.";
      setSubmitError(Array.isArray(message) ? message.join(", ") : message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleToggleView = (offboardingState) => {
    setShowOffboarding(offboardingState);
  };

  const handleSuccessDone = () => {
    setModal(false);
    navigate("/employee");
  };

  const FormContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <PersonalInformation
            data={personalData}
            onDataChange={setPersonalData}
            photoFile={profilePhotoFile}
            onPhotoChange={setProfilePhotoFile}
            errors={personalErrors}
            showErrors={currentStep === 1 && !canProceedCurrentStep}
            generatedEmployeeId={generatedEmployeeId}
          />
        );
      case 2:
        return (
          <EducationForm
            data={educationData}
            onDataChange={setEducationData}
            errors={educationErrors}
            showErrors={currentStep === 2 && !canProceedCurrentStep}
          />
        );
      case 3:
        return <Experience data={experienceData} onDataChange={setExperienceData} />;
      case 4:
        return <Awards data={awardsData} onDataChange={setAwardsData} />;
      case 5:
        return (
          <div className="space-y-5"><OrganisationForm
            data={organizationData}
            onDataChange={setOrganizationData}
            errors={organizationErrors}
            showErrors={currentStep === 5 && !canProceedCurrentStep}
            generatedEmployeeId={generatedEmployeeId}
          /><label className="mx-auto block max-w-4xl rounded-xl border border-indigo-100 bg-indigo-50/50 p-4"><span className="block text-xs font-semibold text-[#071C50] sm:text-sm">Job-description template <span className="font-normal text-slate-400">(optional)</span></span><p className="mb-2 mt-1 text-[10px] text-slate-500 sm:text-xs">Choose a reusable template now. It will be saved as an editable draft for HR to review and issue.</p><select value={jobTemplateId} onChange={(event)=>setJobTemplateId(event.target.value)} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs sm:text-sm"><option value="">Assign later</option>{jobTemplates.map((template)=><option key={template.id} value={template.id}>{template.name} — {template.jobTitle}{template.role?.name?` (${template.role.name})`:""}</option>)}</select></label></div>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <div className="w-full font-Outfit min-h-screen bg-white rounded-md mx-auto p-6">
        <EmployeeToggleHeader onToggleView={handleToggleView} isOffboarding={showOffboarding} />

        <div className="lg:flex justify-between my-8 items-center">
          <StepIndicator currentStep={currentStep} />
          <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
        </div>

        {submitError ? (
          <p className="mb-4 text-sm text-red-600" role="alert">
            {submitError}
          </p>
        ) : null}

        {FormContent()}

        <div className="flex justify-center gap-4 items-center mt-12">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={currentStep === 1 || isSubmitting}
            className="px-6 py-3 border w-40 border-[#1234CD] text-[#1234CD] rounded-lg disabled:opacity-50"
          >
            Previous
          </button>

          {currentStep === totalSteps ? (
            <button
              type="button"
              disabled={isSubmitting || !canProceedCurrentStep}
              onClick={handleSubmit}
              className="px-6 w-40 py-3 bg-[#1234CD] text-white rounded-lg disabled:opacity-60"
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          ) : (
            <button
              type="button"
              disabled={isSubmitting || !canProceedCurrentStep}
              onClick={handleNext}
              className="px-6 w-40 py-3 bg-[#1234CD] text-white rounded-lg disabled:opacity-60"
            >
              Next
            </button>
          )}
        </div>
      </div>

      <SuccessModal isOpen={modal} onClose={() => setModal(false)} onDone={handleSuccessDone} doneLabel="Back to Staff List">
        <div className="text-center py-6 font-Outfit">
          <p className="text-xl font-semibold text-[#071C50]">Staff Onboarding Completed</p>
          <p className="text-sm text-gray-600 mt-2">The staff details were saved successfully.</p>
          {generatedEmployeeId ? (
            <p className="text-sm text-[#1234CD] mt-3">Employee ID: {generatedEmployeeId}</p>
          ) : null}
        </div>
      </SuccessModal>
    </div>
  );
};

export default NewEmployee;
